/**
 * @name IdentificacaoAluno
 * @author Keven Adan Bezerra | kevenadanb
 * @description Componente para identificação automática do aluno via campo único (RFID ou código)
 * Implementa o requisito RF01 com detecção automática e fallback
 * @param {IdentificacaoAlunoProps} props - Props do componente
 * @returns {JSX.Element} Interface de identificação otimizada para totem
 * @package src/components/pages/IdentificacaoAluno.tsx
 */

import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef } from "react";

import { StyledTextField } from "@/components/common/fields";
import RfidDebugInfo from "@/components/debug/RfidDebugInfo";
import { useUserContext } from "@/contexts/UserContext";
import { useRfidReaderCapture } from "@/hooks/useRfidReaderCapture";
import useThemeContext from "@/themes/hooks/useThemeContext";
import { getGlassButton, getGlassCard } from "@/themes/styles/glassStyles";
import { useIdentificacaoAluno } from "./useIdentificacaoAluno";

interface IdentificacaoAlunoProps {
  onAlunoIdentificado: () => void;
}

const IdentificacaoAluno: React.FC<IdentificacaoAlunoProps> = ({
  onAlunoIdentificado,
}) => {
  const { alunoAtual } = useUserContext();
  const { theme } = useThemeContext();

  // Hook customizado com toda a lógica de identificação
  const {
    identificador,
    isCarregando,
    setIdentificador,
    handleIdentificar,
    handleKeyPress,
    placeholder,
    label,
    instrucoes,
  } = useIdentificacaoAluno();
  // Ref para armazenar o valor real digitado
  const realValueRef = useRef<string>("");

  // Hook para captura robusta de dados RFID
  const rfidCapture = useRfidReaderCapture({
    minLength: 3, // Mínimo 3 caracteres
    maxLength: 20, // Máximo 20 caracteres
    autoSubmitDelay: 300, // 300ms após captura completa
    debounceTime: 50, // 50ms de debounce
  });

  // Configurar callback para quando dados RFID completos são recebidos
  useEffect(() => {
    rfidCapture.setOnDataReceived((data) => {
      console.log("📡 Dados RFID completos recebidos:", data);
      realValueRef.current = data;

      // ⚡ CORREÇÃO: Não processa zeros aqui, deixa valor bruto
      setIdentificador(data);

      // Auto-identificar após receber dados do leitor
      setTimeout(() => {
        console.log("🤖 Auto-identificação iniciada");
        handleIdentificar();
      }, 100);
    });
  }, [rfidCapture, setIdentificador, handleIdentificar]);

  // Handler melhorado para capturar entrada de caracteres do leitor RFID
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;

      // Detectar se é entrada de leitor RFID (múltiplos caracteres rapidamente)
      const isRfidInput = input.length > realValueRef.current.length + 1;

      if (isRfidInput) {
        // Entrada rápida do leitor RFID
        console.log("📡 Entrada RFID detectada:", input);
        realValueRef.current = input;

        // ⚡ CORREÇÃO: Não processa zeros aqui, deixa valor bruto
        setIdentificador(input);

        // Auto-identificar para entrada RFID
        setTimeout(() => {
          console.log("🤖 Auto-identificação para entrada RFID");
          handleIdentificar();
        }, 200);
      } else {
        // Entrada manual normal
        if (input.length < realValueRef.current.length) {
          realValueRef.current = realValueRef.current.slice(0, input.length);
        } else {
          const diff = input.length - realValueRef.current.length;
          if (
            diff > 0 &&
            e.nativeEvent instanceof InputEvent &&
            e.nativeEvent.data
          ) {
            realValueRef.current += e.nativeEvent.data;
          }
        }

        // ⚡ CORREÇÃO: Não processa zeros aqui, deixa valor bruto
        setIdentificador(realValueRef.current);
      }
    },
    [setIdentificador, handleIdentificar],
  );

  // Handler para teclas especiais do leitor RFID
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Detectar caracteres de controle que podem vir do leitor RFID
      const char = e.key;

      if (char.length === 1) {
        // Processar caractere através do sistema de captura RFID
        rfidCapture.processInput(char);
      }

      // Permitir comportamento normal do teclado
      if (e.key === "Enter") {
        handleIdentificar();
      }
    },
    [rfidCapture, handleIdentificar],
  );

  // Sincronizar realValueRef com o identificador
  useEffect(() => {
    realValueRef.current = identificador;
  }, [identificador]);

  // Efeito para monitorar mudanças no estado do aluno
  useEffect(() => {
    console.log("👤 Estado do aluno atualizado:", {
      temAluno: !!alunoAtual,
      timestamp: new Date().toISOString(),
    });

    if (alunoAtual && !isCarregando) {
      console.log("✅ Aluno identificado no estado, navegando...");
      onAlunoIdentificado();
    }
  }, [alunoAtual, isCarregando]); // Removido onAlunoIdentificado para evitar loops

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "transparent",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: "480px", // Largura ideal para identificação
          width: "100%",
          px: 2,
          color: "transparent",
        }}
      >
        <Card
          sx={{
            p: 4,
            ...getGlassCard(theme, "strong"),
            borderRadius: 3,
          }}
        >
          <CardContent>
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                🍽️ IS Cantina
              </Typography>
              <Typography variant="h5" color="text.secondary">
                Identifique-se para começar
              </Typography>
            </Box>
            {/* Indicador removido - agora é busca sequencial simples */}
            {/* Campo Único de Identificação */}
            <StyledTextField
              fullWidth
              label={label}
              placeholder={placeholder}
              value={identificador}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onKeyPress={handleKeyPress}
              disabled={isCarregando}
              autoFocus
              height={56}
              fontSize="1.1rem"
              marginBottom={3}
              withGlassContainer={false}
              inputProps={{
                autoComplete: "off",
                "data-testid": "rfid-input",
              }}
            />
            {/* Botão de Identificação */}
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleIdentificar}
              disabled={!identificador.trim() || isCarregando}
              sx={{
                ...getGlassButton(theme, "primary"),
                height: 56,
                fontSize: "1.1rem",
                fontWeight: "bold",
                position: "relative",
                "&.Mui-disabled": {
                  backgroundColor: isCarregando
                    ? "primary.main"
                    : "action.disabledBackground",
                  color: isCarregando
                    ? "primary.contrastText"
                    : "action.disabled",
                  opacity: isCarregando ? 0.8 : 0.5,
                },
              }}
            >
              {isCarregando ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography>Identificando</Typography>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 4,
                        height: 4,
                        bgcolor: "common.white",
                        borderRadius: "50%",
                        animation: "pulse 1s infinite",
                        "@keyframes pulse": {
                          "0%": { opacity: 0.2 },
                          "20%": { opacity: 1 },
                          "100%": { opacity: 0.2 },
                        },
                        animationDelay: "0s",
                      }}
                    />
                    <Box
                      sx={{
                        width: 4,
                        height: 4,
                        bgcolor: "common.white",
                        borderRadius: "50%",
                        animation: "pulse 1s infinite",
                        animationDelay: "0.2s",
                      }}
                    />
                    <Box
                      sx={{
                        width: 4,
                        height: 4,
                        bgcolor: "common.white",
                        borderRadius: "50%",
                        animation: "pulse 1s infinite",
                        animationDelay: "0.4s",
                      }}
                    />
                  </Box>
                </Box>
              ) : (
                "Entrar"
              )}
            </Button>
            {/* Instruções Dinâmicas */}
            <Box sx={{ mt: 3, textAlign: "center" }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.6 }}
              >
                <strong>{instrucoes.titulo}</strong>
                <br />
                {instrucoes.subtitulo}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Debug RFID - apenas em desenvolvimento */}
        <RfidDebugInfo
          rfidCapture={rfidCapture}
          realValue={realValueRef.current}
          processedValue={identificador}
        />
      </Container>
    </Box>
  );
};

export default IdentificacaoAluno;
